import { useState } from "react";

import { MAX_LIVE_LEVERAGE } from "@strat/engine";
import { effectiveLeverage } from "@strat/format";
import type { RiskParams, TradeDirection } from "@strat/format";
// The subpath, not the package root: the root pulls in Plotly, which a dialog
// (and the node test that imports RunAsBotButton) has no use for.
import { LeverageControl } from "@strat/ui/leverage";

export type BotMarketType = "spot" | "futures";

/** Quick picks for a paper bot: up to the live cap, nothing above it. */
const BOT_STEPS = [1, 2, 3, 5, 10];

/**
 * The leverage and funding a new bot runs with, starting from the strategy's.
 *
 * Above the live cap the bot starts at the cap rather than refusing to start:
 * a 20× backtest is a fine thing to have, and "run it at 10×" is the useful
 * answer, not a greyed-out button.
 */
export function useBotLeverage(risk: Pick<RiskParams, "leverage" | "applyFunding"> | undefined) {
  const strategyLeverage = effectiveLeverage(risk ?? {});
  const [leverage, setLeverage] = useState(Math.min(MAX_LIVE_LEVERAGE, strategyLeverage));
  const [funding, setFunding] = useState(risk?.applyFunding === true);
  return {
    strategyLeverage,
    strategyFunding: risk?.applyFunding === true,
    leverage,
    setLeverage,
    funding,
    setFunding,
    /**
     * What to send with the deploy: only what differs from the strategy, so an
     * unchanged deploy keeps running exactly the published spec. Spot is 1×
     * without funding, whatever the strategy says.
     */
    overrides(marketType: BotMarketType): { leverage?: number; applyFunding?: boolean } {
      const lev = marketType === "spot" ? 1 : leverage;
      const fund = marketType === "spot" ? false : funding;
      return {
        ...(lev !== strategyLeverage ? { leverage: lev } : {}),
        ...(fund !== (risk?.applyFunding === true) ? { applyFunding: fund } : {}),
      };
    },
  };
}

export function MarketTypeToggle({
  value,
  onChange,
}: {
  value: BotMarketType;
  onChange: (next: BotMarketType) => void;
}) {
  return (
    <div className="field">
      <span className="field__label" id="bot-market-type">
        Market type
      </span>
      <div className="segmented bot-market-type" role="radiogroup" aria-labelledby="bot-market-type">
        {(
          [
            ["spot", "Spot", "your own cash, no leverage"],
            ["futures", "Futures", "USD-M perpetual, leverage and funding"],
          ] as const
        ).map(([key, label, hint]) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={value === key}
            className={value === key ? "is-active" : undefined}
            title={hint}
            onClick={() => onChange(key)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The leverage block of a deploy dialog, with funding under it on futures. */
export function BotLeverageFields({
  marketType,
  onMarketTypeChange,
  state,
  direction,
  margin,
  stopLossPct,
}: {
  marketType: BotMarketType;
  onMarketTypeChange: (next: BotMarketType) => void;
  state: ReturnType<typeof useBotLeverage>;
  direction: TradeDirection;
  margin: number | null;
  stopLossPct?: number;
}) {
  const capped = state.strategyLeverage > MAX_LIVE_LEVERAGE;
  const changed = marketType === "futures" && state.leverage !== state.strategyLeverage;
  return (
    <div className="bot-leverage">
      <LeverageControl
        value={marketType === "spot" ? state.strategyLeverage : state.leverage}
        onChange={state.setLeverage}
        direction={direction}
        marketType={marketType}
        max={MAX_LIVE_LEVERAGE}
        steps={BOT_STEPS}
        margin={margin}
        stopLossPct={stopLossPct}
        spotForcesOne
        onSwitchToFutures={() => onMarketTypeChange("futures")}
        futuresLabel="futures"
        note={
          capped
            ? `The strategy is set to ${state.strategyLeverage}×; paper bots run up to ${MAX_LIVE_LEVERAGE}×.`
            : changed
              ? `The strategy is set to ${state.strategyLeverage}×; this bot runs at ${state.leverage}×. The strategy itself is not changed.`
              : `Paper bots run up to ${MAX_LIVE_LEVERAGE}×.`
        }
      />
      {marketType === "futures" && (
        <label className="checkbox checkbox--wrap bot-leverage__funding">
          <input
            type="checkbox"
            checked={state.funding}
            onChange={(e) => state.setFunding(e.target.checked)}
          />
          <span>
            Charge perpetual funding{" "}
            <span className="muted small">
              — paid or received at every settlement, as on the exchange
              {state.funding !== state.strategyFunding ? " (differs from the strategy)" : ""}
            </span>
          </span>
        </label>
      )}
    </div>
  );
}
